
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import {
  Box,
  Button,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  useMemo,
  useState,
  type ElementType,
} from "react";

import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

import { useAnchorCubes } from "../hooks/useAnchorCubes";

import ImpactColumnMediaUpload from "./ImpactColumnMediaUpload";

import {
  ImpactQuillField,
  stripImpactHtml,
} from "./ImpactQuillField";

import {
  COLLECTOR_ONLY_SECTIONS,
  CREATOR_ONLY_SECTIONS,
  cubeSectionKey,
  isPartnerImpactCube,
  type AnchorCube,
  type AnchorCubeColumn,
} from "../utils/anchorCubes";

import {
  getImpactCardBookPageOrder,
  type ImpactCardBookSection,
} from "./ImpactCardBookPage";

import { uniqueColumns } from "../utils/impactPages";

import {
  emptyColumnExperience,
  IMPACT_COLUMN_EXPERIENCE_TYPES,
  columnExperienceHasContent,
  columnImageUrls,
  withColumnImages,
  type ImpactColumnExperience,
  type ImpactColumnExperienceType,
} from "../utils/columnExperience";

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

type ImpactCardTalentTocProps = {
  canEdit?: boolean;
  partnerEditOnly?: boolean;
  coverImageUrl?: string;

  onSelectSection?: (
    sectionKey: string
  ) => void;

  onSelectCube?: (
    cube: AnchorCube
  ) => void;

  onSelectColumn?: (
    column: AnchorCubeColumn,
    cube: AnchorCube
  ) => void;
};

/* -------------------------------------------------------------------------- */
/* Theme                                                                      */
/* -------------------------------------------------------------------------- */

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#5E8F72",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  text: "#17231D",
  muted: "#68766F",
  soft: "#89968F",
  border: "rgba(16,44,36,.10)",
  line: "rgba(16,44,36,.12)",
};

/* -------------------------------------------------------------------------- */
/* Field styling                                                              */
/* -------------------------------------------------------------------------- */

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: c.paper,
    fontFamily: HOPE_SANS,

    "& fieldset": {
      borderColor: c.border,
    },

    "&:hover fieldset": {
      borderColor: c.green,
    },

    "&.Mui-focused fieldset": {
      borderColor: c.forest,
    },
  },

  "& .MuiInputLabel-root": {
    fontFamily: HOPE_SANS,
    color: c.soft,
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: c.forest,
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Built-in page mapping                                                      */
/* -------------------------------------------------------------------------- */

const TOC_CUBE_ID_BY_SECTION: Record<
  ImpactCardBookSection,
  string
> = {
  overview: "cube-impact-card",
  funding: "cube-funding",
  recipientPm: "cube-pm-relief",
  recipientAol: "cube-aol",
  contribute: "cube-contribute",
  contributeStatus: "cube-contribute-status",
  thanks: "cube-thanks",
  adminContributors: "cube-contributors",
  people: "cube-community",
};

/* -------------------------------------------------------------------------- */
/* Korean TOC defaults                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Presentation-only Korean labels for built-in pages.
 *
 * Existing cube IDs, backend data, and navigation
 * keys are preserved.
 *
 * Custom page titles and descriptions are not
 * overwritten.
 */
const DEFAULT_KOREAN_TOC: Record<
  string,
  {
    title: string;
    description: string;
  }
> = {
  "cube-impact-card": {
    title: "임팩트 카드 소개",
    description:
      "함께 만드는 변화와 나눔의 이야기",
  },

  "cube-funding": {
    title: "모금 목표",
    description:
      "모금 목표와 기부금 사용 계획",
  },

  "cube-pm-relief": {
    title: "Prime Minister's Relief Fund",
    description:
      "구호 기금 소개와 현장 활동",
  },

  "cube-aol": {
    title: "Art of Living Nepal",
    description:
      "현지 구호 활동과 지원 이야기",
  },

  "cube-contribute": {
    title: "기부 참여",
    description:
      "기부를 약속하고 나눔에 동참해 주세요",
  },

  "cube-contribute-status": {
    title: "기부 현황",
    description:
      "기부 약속 및 입금 확인 현황",
  },

  "cube-thanks": {
    title: "감사의 마음",
    description:
      "Art of Living Nepal과 후원자가 전하는 감사의 이야기",
  },

  "cube-contributors": {
    title: "기부자 현황 관리",
    description:
      "기부 약속과 입금 확인 내역 관리",
  },

  "cube-community": {
    title: "함께하는 사람들",
    description:
      "이 캠페인에 함께하는 사람들의 이야기",
  },
};

function getTocDisplayTitle(
  cube: AnchorCube
): string {
  return (
    DEFAULT_KOREAN_TOC[cube.id]?.title ??
    stripImpactHtml(cube.title)
  );
}

function getTocDisplayDescription(
  cube: AnchorCube
): string {
  return (
    DEFAULT_KOREAN_TOC[cube.id]?.description ??
    stripImpactHtml(cube.description)
  );
}

/* -------------------------------------------------------------------------- */
/* Korean content type labels                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Only the displayed text changes.
 * The underlying experience type remains unchanged.
 */
const KOREAN_CONTENT_TYPES: Record<
  ImpactColumnExperienceType,
  {
    label: string;
    description: string;
  }
> = {
  Text: {
    label: "텍스트",
    description:
      "이야기와 설명을 작성합니다.",
  },
  Audio: {
    label: "오디오",
    description:
      "오디오 파일을 추가합니다.",
  },


  Image: {
    label: "이미지",
    description:
      "사진이나 이미지를 추가합니다.",
  },

  Video: {
    label: "동영상",
    description:
      "동영상 파일을 추가합니다.",
  },

  Youtube: {
    label: "유튜브",
    description:
      "유튜브 영상을 연결합니다.",
  },

  Location: {
    label: "위치",
    description:
      "장소와 지도 정보를 추가합니다.",
  },
};

/* -------------------------------------------------------------------------- */
/* Fallback contribution status cube                                          */
/* -------------------------------------------------------------------------- */

const CONTRIBUTE_STATUS_CUBE: AnchorCube = {
  id: "cube-contribute-status",
  title: "기부 현황",
  description:
    "기부 약속 및 입금 확인 현황",
  icon: "shield",
  tone: "gold",
  locked: true,
  collapsed: true,
  columns: [],
};

/* -------------------------------------------------------------------------- */
/* Role-based TOC ordering                                                    */
/* -------------------------------------------------------------------------- */

function orderTocCubesForRole(
  cubes: AnchorCube[],
  isCreator: boolean,
  isPartner: boolean
): AnchorCube[] {
  const byId = new Map(
    cubes.map((cube) => [cube.id, cube])
  );

  const used = new Set<string>();

  const ordered: AnchorCube[] = [];

  for (
    const section of getImpactCardBookPageOrder({
      isCreator,
      isPartner,
    })
  ) {
    const cubeId =
      TOC_CUBE_ID_BY_SECTION[section];

    const cube =
      byId.get(cubeId) ??
      (
        section === "contributeStatus"
          ? CONTRIBUTE_STATUS_CUBE
          : undefined
      );

    if (
      !cube ||
      used.has(cube.id)
    ) {
      continue;
    }

    used.add(cube.id);

    ordered.push(cube);
  }

  for (const cube of cubes) {
    if (used.has(cube.id)) {
      continue;
    }

    if (
      cube.locked ||
      cube.id === "cube-overview"
    ) {
      continue;
    }

    const section = cubeSectionKey(cube);

    if (
      section === "updates" ||
      section === "overview"
    ) {
      continue;
    }

    if (
      CREATOR_ONLY_SECTIONS.has(
        section ?? ""
      ) &&
      !isCreator
    ) {
      continue;
    }

    if (
      COLLECTOR_ONLY_SECTIONS.has(
        section ?? ""
      ) &&
      (isCreator || isPartner)
    ) {
      continue;
    }

    ordered.push(cube);
  }

  return ordered;
}

/* -------------------------------------------------------------------------- */
/* Icons                                                                      */
/* -------------------------------------------------------------------------- */

const ICONS: Record<
  string,
  ElementType
> = {
  target: TrackChangesRoundedIcon,
  leaf: SpaRoundedIcon,
  school: SchoolRoundedIcon,
  alert: WarningAmberRoundedIcon,
  book: MenuBookRoundedIcon,
  people: PeopleAltRoundedIcon,
  doc: DescriptionOutlinedIcon,
  heart: FavoriteRoundedIcon,
  groups: GroupsRoundedIcon,
  give: VolunteerActivismRoundedIcon,
  coins: SavingsOutlinedIcon,
  calendar: CalendarMonthRoundedIcon,
  shield: VerifiedUserOutlinedIcon,
  community: GroupsRoundedIcon,
  chat: ChatBubbleOutlineRoundedIcon,
  megaphone: CampaignRoundedIcon,
  image: ImageOutlinedIcon,
  cube: ViewInArRoundedIcon,
  youtube: YouTubeIcon,
  audio: MusicNoteOutlinedIcon,
  video: VideocamOutlinedIcon,
  pin: LocationOnOutlinedIcon,
};

function CubeIcon({
  name,
  color,
  size = 18,
}: {
  name?: string;
  color: string;
  size?: number;
}) {
  const Icon =
    ICONS[name ?? "cube"] ??
    ViewInArRoundedIcon;

  return (
    <Icon
      sx={{
        fontSize: size,
        color,
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function ImpactCardTalentToc({
  canEdit = false,
  partnerEditOnly = false,
  coverImageUrl: _coverImageUrl,
  onSelectSection,
  onSelectCube,
  onSelectColumn,
}: ImpactCardTalentTocProps) {
  const {
    cubes,
    saving,
    addCube,
    editCube,
    addColumn,
    editColumn,
    toggleCube,
    removeCube,
    removeColumn,
  } = useAnchorCubes();

  /* ------------------------------------------------------------------------ */
  /* State                                                                    */
  /* ------------------------------------------------------------------------ */

  const [
    contentDialogOpen,
    setContentDialogOpen,
  ] = useState(false);

  const [
    dialogKind,
    setDialogKind,
  ] = useState<"cube" | "column">(
    "column"
  );

  const [
    editingCubeId,
    setEditingCubeId,
  ] = useState<string | null>(null);

  const [
    insertAfterCubeId,
    setInsertAfterCubeId,
  ] = useState<string | null>(null);

  const [
    columnDialogCubeId,
    setColumnDialogCubeId,
  ] = useState<string | null>(null);

  const [
    editingColumnId,
    setEditingColumnId,
  ] = useState<string | null>(null);

  const [
    insertAfterColumnId,
    setInsertAfterColumnId,
  ] = useState<string | null>(null);

  const [
    draftTitle,
    setDraftTitle,
  ] = useState("");

  const [
    draftDescription,
    setDraftDescription,
  ] = useState("");

  const [
    draftExperienceType,
    setDraftExperienceType,
  ] = useState<
    ImpactColumnExperienceType | undefined
  >();

  const [
    draftExperience,
    setDraftExperience,
  ] = useState<ImpactColumnExperience>(
    emptyColumnExperience()
  );

  const [
    menu,
    setMenu,
  ] = useState<{
    anchor: HTMLElement;
    cubeId: string;
    columnId?: string;
  } | null>(null);

  /* ------------------------------------------------------------------------ */
  /* Roles                                                                    */
  /* ------------------------------------------------------------------------ */

  const isCreator =
    canEdit && !partnerEditOnly;

  const isPartner =
    partnerEditOnly;

  const visibleCubes = useMemo(
    () =>
      orderTocCubesForRole(
        cubes,
        isCreator,
        isPartner
      ),
    [
      cubes,
      isCreator,
      isPartner,
    ]
  );

  const canManageCube = (
    cube: AnchorCube
  ) =>
    canEdit &&
    (
      !partnerEditOnly ||
      isPartnerImpactCube(cube)
    );

  /* ------------------------------------------------------------------------ */
  /* Editing data                                                             */
  /* ------------------------------------------------------------------------ */

  const editingCube = useMemo(
    () =>
      cubes.find(
        (cube) =>
          cube.id === editingCubeId
      ) ?? null,
    [
      cubes,
      editingCubeId,
    ]
  );

  const columnDialogCube = useMemo(
    () =>
      cubes.find(
        (cube) =>
          cube.id === columnDialogCubeId
      ) ?? null,
    [
      cubes,
      columnDialogCubeId,
    ]
  );

  const editingColumn = useMemo(() => {
    if (
      !columnDialogCube ||
      !editingColumnId
    ) {
      return null;
    }

    return (
      columnDialogCube.columns.find(
        (column) =>
          column.id === editingColumnId
      ) ?? null
    );
  }, [
    columnDialogCube,
    editingColumnId,
  ]);

  const isPageDialog =
    dialogKind === "cube";

  const isEditingContent =
    Boolean(
      isPageDialog
        ? editingCube
        : editingColumn
    );

  /* ------------------------------------------------------------------------ */
  /* Korean dialog copy                                                       */
  /* ------------------------------------------------------------------------ */

  const dialogCopy = isPageDialog
    ? isEditingContent
      ? {
          title: "페이지 수정",
          subtitle:
            "페이지 제목과 내용을 수정해 주세요.",
          titleLabel: "페이지 제목",
          action: "저장",
        }
      : {
          title: "새 페이지 추가",
          subtitle:
            "목차에 새로운 페이지를 추가해 주세요.",
          titleLabel: "페이지 제목",
          action: "페이지 추가",
        }
    : isEditingContent
      ? {
          title: "하위 페이지 수정",
          subtitle: `${
            columnDialogCube
              ? getTocDisplayTitle(
                  columnDialogCube
                )
              : "선택한 페이지"
          }의 하위 페이지를 수정해 주세요.`,
          titleLabel:
            "하위 페이지 제목",
          action: "저장",
        }
      : {
          title: "하위 페이지 추가",
          subtitle: `${
            columnDialogCube
              ? getTocDisplayTitle(
                  columnDialogCube
                )
              : "선택한 페이지"
          }에 하위 페이지를 추가해 주세요.`,
          titleLabel:
            "하위 페이지 제목",
          action: "하위 페이지 추가",
        };

  /* ------------------------------------------------------------------------ */
  /* Dialog helpers                                                           */
  /* ------------------------------------------------------------------------ */

  const resetDraft = () => {
    setDraftTitle("");
    setDraftDescription("");

    setDraftExperienceType(
      undefined
    );

    setDraftExperience(
      emptyColumnExperience()
    );
  };

  const closeContentDialog = () => {
    resetDraft();

    setContentDialogOpen(false);

    setEditingCubeId(null);
    setInsertAfterCubeId(null);

    setColumnDialogCubeId(null);
    setEditingColumnId(null);
    setInsertAfterColumnId(null);
  };

  const openNewCube = (
    afterCubeId?: string
  ) => {
    resetDraft();

    setDialogKind("cube");

    setEditingCubeId(null);

    setInsertAfterCubeId(
      afterCubeId ?? null
    );

    setColumnDialogCubeId(null);
    setEditingColumnId(null);

    setContentDialogOpen(true);
  };

  const openEditCube = (
    cubeId: string
  ) => {
    const cube = cubes.find(
      (item) =>
        item.id === cubeId
    );

    if (
      !cube ||
      !canManageCube(cube)
    ) {
      return;
    }

    setDraftTitle(
      cube.title ?? ""
    );

    setDraftDescription(
      cube.description ?? ""
    );

    setDraftExperienceType(
      cube.experienceType
    );

    setDraftExperience(
      cube.experience ??
        emptyColumnExperience()
    );

    setDialogKind("cube");

    setEditingCubeId(cubeId);

    setInsertAfterCubeId(null);

    setColumnDialogCubeId(null);
    setEditingColumnId(null);

    setContentDialogOpen(true);
  };

  const openAddColumn = (
    cubeId: string,
    afterColumnId?: string
  ) => {
    const cube = cubes.find(
      (item) =>
        item.id === cubeId
    );

    if (
      !cube ||
      !canManageCube(cube)
    ) {
      return;
    }

    resetDraft();

    setDialogKind("column");

    setEditingColumnId(null);

    setInsertAfterColumnId(
      afterColumnId ?? null
    );

    setColumnDialogCubeId(
      cubeId
    );

    setEditingCubeId(null);

    setContentDialogOpen(true);
  };

  const openEditColumn = (
    cubeId: string,
    columnId: string
  ) => {
    const cube = cubes.find(
      (item) =>
        item.id === cubeId
    );

    if (
      !cube ||
      !canManageCube(cube)
    ) {
      return;
    }

    const column =
      cube.columns.find(
        (item) =>
          item.id === columnId
      );

    setDraftTitle(
      column?.title ?? ""
    );

    setDraftDescription(
      column?.description ?? ""
    );

    setDraftExperienceType(
      column?.experienceType
    );

    setDraftExperience(
      column?.experience ??
        emptyColumnExperience()
    );

    setDialogKind("column");

    setEditingColumnId(
      columnId
    );

    setColumnDialogCubeId(
      cubeId
    );

    setEditingCubeId(null);

    setContentDialogOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /* Save                                                                     */
  /* ------------------------------------------------------------------------ */

  const submitContent = async () => {
    try {
      if (
        dialogKind === "cube"
      ) {
        if (editingCubeId) {
          const cube = cubes.find(
            (item) =>
              item.id === editingCubeId
          );

          if (
            !cube ||
            !canManageCube(cube)
          ) {
            return;
          }

          await editCube(
            editingCubeId,
            {
              title: draftTitle,
              description:
                draftDescription,
              experienceType:
                draftExperienceType,
              experience:
                draftExperience,
            }
          );
        } else {
          await addCube({
            title: draftTitle,
            description:
              draftDescription,

            afterCubeId:
              insertAfterCubeId ??
              undefined,

            experienceType:
              draftExperienceType,

            experience:
              draftExperience,

            creator:
              partnerEditOnly
                ? "PARTNER"
                : undefined,
          });
        }
      } else {
        if (
          !columnDialogCubeId
        ) {
          return;
        }

        const parent =
          cubes.find(
            (item) =>
              item.id ===
              columnDialogCubeId
          );

        if (
          !parent ||
          !canManageCube(parent)
        ) {
          return;
        }

        if (
          editingColumnId
        ) {
          await editColumn(
            columnDialogCubeId,
            editingColumnId,
            {
              title: draftTitle,
              description:
                draftDescription,

              experienceType:
                draftExperienceType,

              experience:
                draftExperience,
            }
          );
        } else {
          await addColumn(
            columnDialogCubeId,
            {
              title: draftTitle,
              description:
                draftDescription,

              afterColumnId:
                insertAfterColumnId ??
                undefined,

              experienceType:
                draftExperienceType,

              experience:
                draftExperience,
            }
          );
        }
      }

      closeContentDialog();
    } catch {
      /*
       * Keep the dialog open so the
       * creator or partner can retry.
       */
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Navigation                                                               */
  /* ------------------------------------------------------------------------ */

  const openCubePage = (
    cube: AnchorCube
  ) => {
    const sectionKey =
      cubeSectionKey(cube);

    if (sectionKey) {
      onSelectSection?.(
        sectionKey
      );

      return;
    }

    onSelectCube?.(
      cube
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
      }}
    >
      {/* ================================================================ */}
      {/* TOC header                                                       */}
      {/* ================================================================ */}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: {
            xs: 2,
            sm: 2.25,
          },

          py: 1.35,

          mb: 0,

          bgcolor: "#F3EFE4",
        }}
      >
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontWeight: 700,
            fontSize: "1.35rem",
            color: c.forestDeep,
          }}
        >
          목차
        </Typography>

        {canEdit ? (
          <IconButton
            aria-label="새 페이지 추가"
            onClick={() =>
              openNewCube()
            }
            sx={{
              width: 36,
              height: 36,

              bgcolor: c.forest,
              color: "#FFFFFF",

              "&:hover": {
                bgcolor:
                  c.forestDeep,
              },
            }}
          >
            <AddRoundedIcon
              sx={{
                fontSize: 22,
              }}
            />
          </IconButton>
        ) : null}
      </Stack>

      {/* ================================================================ */}
      {/* TOC list                                                         */}
      {/* ================================================================ */}

      <Stack
        spacing={0}
        sx={{
          px: {
            xs: 2,
            sm: 2.25,
          },

          pt: 0.5,
          pb: 2,
        }}
      >
        {visibleCubes.map(
          (cube, index) => {
            const visibleColumns =
              uniqueColumns(
                cube.columns.filter(
                  (column) =>
                    columnExperienceHasContent(
                      column.experienceType,
                      column.experience
                    ) ||
                    Boolean(
                      column.pageId
                    ) ||
                    Boolean(
                      stripImpactHtml(
                        column.title
                      )
                    )
                )
              );

            const collapsed =
              cube.collapsed !==
              false;

            const imageThumb =
              cube.experienceType ===
              "Image"
                ? columnImageUrls(
                    cube.experience
                  )[0] ||
                  cube.thumbnailUrl
                : undefined;

            const displayTitle =
              getTocDisplayTitle(
                cube
              );

            const displayDescription =
              getTocDisplayDescription(
                cube
              );

            return (
              <Box
                key={cube.id}
              >
                {/* ---------------------------------------------------- */}
                {/* Main page                                            */}
                {/* ---------------------------------------------------- */}

                <Box
                  onClick={() =>
                    openCubePage(
                      cube
                    )
                  }
                  sx={{
                    display:
                      "flex",

                    alignItems:
                      "center",

                    minHeight: 64,

                    py: 1,

                    pl: 0.25,
                    pr: 0.25,

                    gap: 1,

                    cursor:
                      "pointer",

                    "&:hover": {
                      bgcolor:
                        "rgba(16,44,36,.03)",
                    },
                  }}
                >
                  {/* Page number */}

                  <Typography
                    sx={{
                      width: 28,
                      flexShrink: 0,

                      fontFamily:
                        HOPE_SANS,

                      fontWeight: 800,

                      fontSize:
                        "0.92rem",

                      letterSpacing:
                        "0.04em",

                      color:
                        c.muted,

                      fontVariantNumeric:
                        "tabular-nums",

                      textAlign:
                        "center",
                    }}
                  >
                    {String(
                      index + 1
                    ).padStart(
                      2,
                      "0"
                    )}
                  </Typography>

                  {/* Main page text */}

                  <Box
                    sx={{
                      flex: 1,

                      minWidth: 0,

                      display:
                        "flex",

                      flexDirection:
                        "column",

                      justifyContent:
                        "center",

                      py: 0.25,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.35}
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      {visibleColumns.length >
                      0 ? (
                        <IconButton
                          size="small"
                          aria-label={
                            collapsed
                              ? "하위 페이지 펼치기"
                              : "하위 페이지 접기"
                          }
                          onClick={(
                            event
                          ) => {
                            event.stopPropagation();

                            toggleCube(
                              cube.id
                            );
                          }}
                          sx={{
                            p: 0.15,
                            mr: 0.15,

                            color:
                              c.soft,

                            "&:hover": {
                              bgcolor:
                                c.mist,

                              color:
                                c.forest,
                            },
                          }}
                        >
                          <KeyboardArrowDownRoundedIcon
                            sx={{
                              fontSize:
                                20,

                              transform:
                                collapsed
                                  ? "rotate(-90deg)"
                                  : "none",

                              transition:
                                "transform .18s ease",
                            }}
                          />
                        </IconButton>
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <Typography
                        noWrap
                        sx={{
                          fontFamily:
                            HOPE_SANS,

                          fontWeight:
                            800,

                          fontSize:
                            "1.12rem",

                          color:
                            c.forestDeep,

                          lineHeight:
                            1.25,

                          minWidth: 0,

                          flex: 1,
                        }}
                      >
                        {displayTitle}
                      </Typography>
                    </Stack>

                    {displayDescription ? (
                      <Typography
                        noWrap
                        sx={{
                          mt: 0.2,

                          pl:
                            "26px",

                          fontFamily:
                            HOPE_SANS,

                          fontSize:
                            "0.9rem",

                          color:
                            "rgba(16,44,36,.42)",

                          lineHeight:
                            1.3,
                        }}
                      >
                        {displayDescription}
                      </Typography>
                    ) : null}
                  </Box>

                  {/* Thumbnail and menu */}

                  <Box
                    sx={{
                      flexShrink: 0,

                      display:
                        "flex",

                      alignItems:
                        "center",

                      gap: 0.25,
                    }}
                  >
                    {imageThumb ? (
                      <Box
                        sx={{
                          width: 56,
                          height: 56,

                          borderRadius:
                            "10px",

                          overflow:
                            "hidden",

                          flexShrink: 0,

                          bgcolor:
                            c.mist,
                        }}
                      >
                        <Box
                          component="img"
                          src={
                            imageThumb
                          }
                          alt=""
                          sx={{
                            width:
                              "100%",

                            height:
                              "100%",

                            objectFit:
                              "contain",

                            display:
                              "block",
                          }}
                        />
                      </Box>
                    ) : null}

                    {canManageCube(
                      cube
                    ) ? (
                      <IconButton
                        size="small"
                        aria-label={`${displayTitle} 관리`}
                        onClick={(
                          event
                        ) => {
                          event.stopPropagation();

                          setMenu({
                            anchor:
                              event.currentTarget,

                            cubeId:
                              cube.id,
                          });
                        }}
                        sx={{
                          color:
                            c.soft,

                          p: 0.35,
                        }}
                      >
                        <MoreVertRoundedIcon
                          sx={{
                            fontSize:
                              18,
                          }}
                        />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>

                {/* ---------------------------------------------------- */}
                {/* Subpages                                             */}
                {/* ---------------------------------------------------- */}

                {!collapsed &&
                visibleColumns.length >
                  0 ? (
                  <Box
                    sx={{
                      position:
                        "relative",

                      pb: 0.75,
                    }}
                  >
                    {visibleColumns.map(
                      (
                        column
                      ) => (
                        <Stack
                          key={
                            column.id
                          }
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          onClick={() =>
                            onSelectColumn?.(
                              column,
                              cube
                            )
                          }
                          sx={{
                            pl: 6.25,
                            pr: 0.25,

                            py: 0.7,

                            cursor:
                              "pointer",

                            "&:hover": {
                              bgcolor:
                                "rgba(16,44,36,.03)",
                            },
                          }}
                        >
                          <CubeIcon
                            name={
                              column.icon
                            }
                            color={
                              c.green
                            }
                            size={
                              15
                            }
                          />

                          <Box
                            sx={{
                              flex: 1,

                              minWidth:
                                0,
                            }}
                          >
                            <Typography
                              noWrap
                              sx={{
                                fontFamily:
                                  HOPE_SANS,

                                fontWeight:
                                  650,

                                fontSize:
                                  "0.95rem",

                                color:
                                  c.text,
                              }}
                            >
                              {stripImpactHtml(
                                column.title
                              )}
                            </Typography>

                            {column.description ? (
                              <Typography
                                noWrap
                                sx={{
                                  fontFamily:
                                    HOPE_SANS,

                                  fontSize:
                                    "0.82rem",

                                  color:
                                    c.soft,
                                }}
                              >
                                {stripImpactHtml(
                                  column.description
                                )}
                              </Typography>
                            ) : null}
                          </Box>

                          {canManageCube(
                            cube
                          ) ? (
                            <IconButton
                              size="small"
                              aria-label="하위 페이지 관리"
                              onClick={(
                                event
                              ) => {
                                event.stopPropagation();

                                setMenu({
                                  anchor:
                                    event.currentTarget,

                                  cubeId:
                                    cube.id,

                                  columnId:
                                    column.id,
                                });
                              }}
                              sx={{
                                color:
                                  c.soft,
                              }}
                            >
                              <MoreVertRoundedIcon
                                sx={{
                                  fontSize:
                                    18,
                                }}
                              />
                            </IconButton>
                          ) : null}
                        </Stack>
                      )
                    )}
                  </Box>
                ) : null}
              </Box>
            );
          }
        )}
      </Stack>

      {/* ================================================================ */}
      {/* Management menu                                                  */}
      {/* ================================================================ */}

      <Menu
        anchorEl={
          menu?.anchor
        }
        open={
          Boolean(menu)
        }
        onClose={() =>
          setMenu(null)
        }
        PaperProps={{
          sx: {
            borderRadius:
              "12px",

            border:
              `1px solid ${c.border}`,

            boxShadow:
              "0 12px 28px rgba(16,44,36,.12)",

            minWidth: 148,
          },
        }}
      >
        {menu?.columnId
          ? [
              <MenuItem
                key="edit"
                onClick={() => {
                  if (
                    menu.columnId
                  ) {
                    openEditColumn(
                      menu.cubeId,
                      menu.columnId
                    );
                  }

                  setMenu(null);
                }}
                sx={{
                  fontFamily:
                    HOPE_SANS,

                  fontSize:
                    "0.82rem",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: c.forest,
                  }}
                >
                  <EditOutlinedIcon
                    sx={{
                      fontSize: 18,
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary="수정"
                />
              </MenuItem>,

              <MenuItem
                key="add-below"
                onClick={() => {
                  if (
                    menu.columnId
                  ) {
                    openAddColumn(
                      menu.cubeId,
                      menu.columnId
                    );
                  }

                  setMenu(null);
                }}
                sx={{
                  fontFamily:
                    HOPE_SANS,

                  fontSize:
                    "0.82rem",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: c.forest,
                  }}
                >
                  <PlaylistAddRoundedIcon
                    sx={{
                      fontSize: 18,
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary="아래에 페이지 추가"
                />
              </MenuItem>,

              <MenuItem
                key="delete"
                onClick={() => {
                  if (
                    menu.columnId
                  ) {
                    removeColumn(
                      menu.cubeId,
                      menu.columnId
                    );
                  }

                  setMenu(null);
                }}
                sx={{
                  fontFamily:
                    HOPE_SANS,

                  fontSize:
                    "0.82rem",

                  color:
                    "#9B3A3A",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color:
                      "#9B3A3A",
                  }}
                >
                  <DeleteOutlineRoundedIcon
                    sx={{
                      fontSize: 18,
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary="삭제"
                />
              </MenuItem>,
            ]
          : [
              <MenuItem
                key="edit"
                onClick={() => {
                  if (menu) {
                    openEditCube(
                      menu.cubeId
                    );
                  }

                  setMenu(null);
                }}
                sx={{
                  fontFamily:
                    HOPE_SANS,

                  fontSize:
                    "0.82rem",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: c.forest,
                  }}
                >
                  <EditOutlinedIcon
                    sx={{
                      fontSize: 18,
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary="수정"
                />
              </MenuItem>,

              <MenuItem
                key="add-subpage"
                onClick={() => {
                  if (!menu) {
                    return;
                  }

                  openAddColumn(
                    menu.cubeId
                  );

                  setMenu(null);
                }}
                sx={{
                  fontFamily:
                    HOPE_SANS,

                  fontSize:
                    "0.82rem",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: c.forest,
                  }}
                >
                  <PlaylistAddRoundedIcon
                    sx={{
                      fontSize: 18,
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary="하위 페이지 추가"
                />
              </MenuItem>,

              cubes.find(
                (item) =>
                  item.id ===
                  menu?.cubeId
              )?.locked
                ? null
                : (
                    <MenuItem
                      key="delete"
                      onClick={() => {
                        if (
                          menu
                        ) {
                          removeCube(
                            menu.cubeId
                          );
                        }

                        setMenu(null);
                      }}
                      sx={{
                        fontFamily:
                          HOPE_SANS,

                        fontSize:
                          "0.82rem",

                        color:
                          "#9B3A3A",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,

                          color:
                            "#9B3A3A",
                        }}
                      >
                        <DeleteOutlineRoundedIcon
                          sx={{
                            fontSize: 18,
                          }}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary="삭제"
                      />
                    </MenuItem>
                  ),
            ]}
      </Menu>

      {/* ================================================================ */}
      {/* Content dialog                                                    */}
      {/* ================================================================ */}

      <Dialog
        open={
          contentDialogOpen
        }
        onClose={
          closeContentDialog
        }
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius:
              "18px",

            bgcolor:
              "#FAF8F2",

            border:
              `1px solid ${c.border}`,

            maxHeight:
              "90vh",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,

            pt: 2.5,

            pb: 2.25,

            overflow:
              "auto",
          }}
        >
          <Typography
            sx={{
              fontFamily:
                HOPE_SERIF,

              fontWeight:
                700,

              fontSize:
                "1.15rem",

              color:
                c.forestDeep,
            }}
          >
            {dialogCopy.title}
          </Typography>

          <Typography
            sx={{
              mt: 0.5,

              mb: 2,

              fontFamily:
                HOPE_SANS,

              fontSize:
                "0.78rem",

              color:
                c.muted,
            }}
          >
            {dialogCopy.subtitle}
          </Typography>

          {/* Page title */}

          <TextField
            autoFocus
            fullWidth
            label={
              dialogCopy.titleLabel
            }
            value={
              draftTitle
            }
            onChange={(
              event
            ) =>
              setDraftTitle(
                event.target.value
              )
            }
            sx={{
              ...fieldSx,
              mb: 1.5,
            }}
          />

          {/* Short description */}

          <TextField
            fullWidth
            label="간단한 설명"
            value={
              draftDescription
            }
            onChange={(
              event
            ) =>
              setDraftDescription(
                event.target.value
              )
            }
            sx={{
              ...fieldSx,
              mb: 2,
            }}
          />

          {/* Content type */}

          <Typography
            sx={{
              fontFamily:
                HOPE_SANS,

              fontWeight:
                800,

              fontSize:
                "0.72rem",

              letterSpacing:
                "0.1em",

              color:
                c.forest,

              mb: 1,
            }}
          >
            콘텐츠 유형
          </Typography>

          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm: "repeat(2, minmax(0, 1fr))",
              },

              gap: 1,

              mb: 1.75,
            }}
          >
            {IMPACT_COLUMN_EXPERIENCE_TYPES.map(
              (type) => {
                const selected =
                  draftExperienceType ===
                  type;

                const copy =
                  KOREAN_CONTENT_TYPES[
                    type
                  ];

                return (
                  <Box
                    key={
                      type
                    }
                    component="button"
                    type="button"
                    onClick={() => {
                      setDraftExperienceType(
                        type
                      );

                      if (
                        !draftExperienceType
                      ) {
                        setDraftExperience(
                          emptyColumnExperience()
                        );
                      }
                    }}
                    sx={{
                      textAlign:
                        "left",

                      border:
                        `1.5px solid ${
                          selected
                            ? c.forest
                            : c.border
                        }`,

                      borderRadius:
                        "14px",

                      bgcolor:
                        selected
                          ? c.mist
                          : c.paper,

                      p: 1.25,

                      cursor:
                        "pointer",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily:
                          HOPE_SANS,

                        fontWeight:
                          800,

                        fontSize:
                          "0.86rem",

                        color:
                          c.forestDeep,
                      }}
                    >
                      {copy.label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.35,

                        fontFamily:
                          HOPE_SANS,

                        fontSize:
                          "0.68rem",

                        color:
                          c.muted,

                        lineHeight:
                          1.35,
                      }}
                    >
                      {copy.description}
                    </Typography>
                  </Box>
                );
              }
            )}
          </Box>

          {/* Content title */}

          {draftExperienceType ===
            "Youtube" ||
          draftExperienceType ===
            "Text" ||
          draftExperienceType ===
            "Location" ? (
            <TextField
              fullWidth
              label={
                draftExperienceType ===
                "Location"
                  ? "장소명"
                  : "제목"
              }
              value={
                draftExperience.title ??
                ""
              }
              onChange={(
                event
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    title:
                      event.target.value,
                  })
                )
              }
              sx={{
                ...fieldSx,

                mb: 1.5,
              }}
            />
          ) : null}

          {/* Location address */}

          {draftExperienceType ===
          "Location" ? (
            <TextField
              fullWidth
              label="주소"
              value={
                draftExperience.address ??
                ""
              }
              onChange={(
                event
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    address:
                      event.target.value,
                  })
                )
              }
              sx={{
                ...fieldSx,

                mb: 1.5,
              }}
            />
          ) : null}

          {/* Image upload */}

          {draftExperienceType ===
          "Image" ? (
            <ImpactColumnMediaUpload
              kind="image"
              urls={columnImageUrls(
                draftExperience
              )}
              onUrls={(
                next
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    ...withColumnImages(
                      next
                    ),
                  })
                )
              }
            />
          ) : null}

          {/* Video upload */}

          {draftExperienceType ===
          "Video" ? (
            <ImpactColumnMediaUpload
              kind="video"
              url={
                draftExperience.url
              }
              onUrl={(
                url
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    url,
                  })
                )
              }
            />
          ) : null}

          {/* URL field */}

          {draftExperienceType &&
          draftExperienceType !==
            "Text" &&
          draftExperienceType !==
            "Image" &&
          draftExperienceType !==
            "Video" ? (
            <TextField
              fullWidth
              label={
                draftExperienceType ===
                "Youtube"
                  ? "유튜브 URL"
                  : draftExperienceType ===
                      "Location"
                    ? "지도 링크 (선택 사항)"
                    : "콘텐츠 URL"
              }
              value={
                draftExperience.url ??
                ""
              }
              onChange={(
                event
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    url:
                      event.target.value,
                  })
                )
              }
              sx={{
                ...fieldSx,

                mb: 1.5,
              }}
            />
          ) : null}

          {/* Text story */}

          {draftExperienceType ===
          "Text" ? (
            <ImpactQuillField
              label="이야기"
              value={
                draftExperience.description ??
                ""
              }
              onChange={(
                description
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    description,
                  })
                )
              }
              minHeight={
                200
              }
              placeholder="제목과 이야기를 작성해 주세요."
            />
          ) : draftExperienceType ? (
            <TextField
              fullWidth
              multiline
              minRows={
                2
              }
              label="설명"
              value={
                draftExperience.description ??
                ""
              }
              onChange={(
                event
              ) =>
                setDraftExperience(
                  (prev) => ({
                    ...prev,

                    description:
                      event.target.value,
                  })
                )
              }
              sx={
                fieldSx
              }
            />
          ) : (
            <Typography
              sx={{
                fontFamily:
                  HOPE_SANS,

                fontSize:
                  "0.78rem",

                color:
                  c.muted,
              }}
            >
              위에서 콘텐츠 유형을 선택해
              동영상, 이미지, 텍스트 또는
              위치 정보를 추가해 주세요.
            </Typography>
          )}

          {/* Dialog actions */}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{
              mt: 2,
            }}
          >
            <Button
              onClick={
                closeContentDialog
              }
              sx={{
                textTransform:
                  "none",

                color:
                  c.muted,

                fontFamily:
                  HOPE_SANS,
              }}
            >
              취소
            </Button>

            <Button
              variant="contained"
              disabled={
                saving
              }
              onClick={() =>
                void submitContent()
              }
              sx={{
                textTransform:
                  "none",

                bgcolor:
                  c.forestDeep,

                fontFamily:
                  HOPE_SANS,

                boxShadow:
                  "none",

                "&:hover": {
                  bgcolor:
                    c.forest,

                  boxShadow:
                    "none",
                },
              }}
            >
              {saving
                ? "저장 중…"
                : dialogCopy.action}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}